// Builds the Mapbox paint-property expressions the print-maps preview
// uses for its route + border line layers, sourced from the user's
// cloud-synced route style settings.
//
// The settings flow:
//   server (User.route_style_settings JSONB)
//     → routeStyleSettingsCloudSync.hydrateFromServer (boot + login)
//     → localStorage[ROUTE_STYLE_STORAGE_KEY]
//     → getRouteStyleSettings() in this module
//     → Mapbox `case`-on-`routeStyle` expressions
//
// Mirrors the Ruby resolver `PrintMapRouteStyleResolver` (which the
// backend uses when rendering the final PDF) so the preview line on
// /print_maps/new and the line on the generated PDF render with the
// same colour, width, and opacity. The base width (5) is the one the
// print preview used historically; the user's `thickness` multiplier
// scales it.
//
// One line, no casing. A print map draws the route once — see the
// note on the layer in PrintMapModule for why the second line went.
//
// No DOM and no Mapbox import — the one function that touches a map
// takes it as a duck-typed argument — so this is exercised directly
// from Jest.

import {
  DEFAULT_ROUTE_STYLE_SETTINGS,
  getRouteStyleSettings,
  RouteStyleSettings,
} from './routeStyleSettings';

// Historical preview width. Kept in lock-step with the value
// `PrintMapModule.ts` used before this change so users without
// `route_style_settings` see no visual change.
export const PREVIEW_LINE_BASE_WIDTH = 5;

export interface PrintPreviewPaintExpressions {
  // Mapbox `case`-on-routeStyle expressions, ready to drop into the
  // `paint` object of a `line` layer.
  lineColor: unknown[];
  lineWidth: unknown[];
  lineOpacity: unknown[];
}

/**
 * Reads the user's current settings from localStorage and builds paint
 * expressions for the preview's `-line` layer.
 */
export function buildPrintPreviewPaintExpressions(): PrintPreviewPaintExpressions {
  return buildExpressions(getRouteStyleSettings());
}

/**
 * Pure overload that lets tests inject settings directly (no
 * localStorage). Same logic, no side effects.
 */
export function buildExpressions(settings: RouteStyleSettings): PrintPreviewPaintExpressions {
  // Web stores 4 tabs (main / alt / spur / recording) plus a global
  // hideMileMarkers boolean. Mapbox `case` arms match `properties.routeStyle`
  // which the backend emits as `route` / `alt` / `spur` / `recording` /
  // `transport`. The `transport` arm inherits `main` (matches the
  // browser hike-map's behaviour — there's no separate "transport"
  // tab in the UI).
  const s = settings;

  return {
    lineColor: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     s.main.color,
      ['==', ['get', 'routeStyle'], 'alt'],        s.alt.color,
      ['==', ['get', 'routeStyle'], 'spur'],       s.spur.color,
      ['==', ['get', 'routeStyle'], 'recording'],  s.recording.color,
      ['==', ['get', 'routeStyle'], 'transport'],  s.main.color,
      s.main.color,
    ],
    lineWidth: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     PREVIEW_LINE_BASE_WIDTH * s.main.thickness,
      ['==', ['get', 'routeStyle'], 'alt'],        PREVIEW_LINE_BASE_WIDTH * s.alt.thickness,
      ['==', ['get', 'routeStyle'], 'spur'],       PREVIEW_LINE_BASE_WIDTH * s.spur.thickness,
      ['==', ['get', 'routeStyle'], 'recording'],  PREVIEW_LINE_BASE_WIDTH * s.recording.thickness,
      ['==', ['get', 'routeStyle'], 'transport'],  PREVIEW_LINE_BASE_WIDTH * s.main.thickness,
      PREVIEW_LINE_BASE_WIDTH,
    ],
    lineOpacity: [
      'case',
      ['==', ['get', 'routeStyle'], 'route'],     s.main.opacity / 100,
      ['==', ['get', 'routeStyle'], 'alt'],        s.alt.opacity / 100,
      ['==', ['get', 'routeStyle'], 'spur'],       s.spur.opacity / 100,
      ['==', ['get', 'routeStyle'], 'recording'],  s.recording.opacity / 100,
      ['==', ['get', 'routeStyle'], 'transport'],  s.main.opacity / 100,
      1.0,
    ],
  };
}

/** One `setPaintProperty` call, described rather than performed. */
export interface PrintPreviewPaintUpdate {
  layerId: string;
  property: string;
  value: unknown;
}

/**
 * The paint properties that have to change for the preview's route line to
 * pick up an edited style, for the layer id `addFeatureCollection` creates.
 *
 * Split out from the applier below so the mapping — which layer gets which
 * expression — is exercised without a Mapbox instance.
 */
export function printPreviewPaintUpdates(
  sourceId: string,
  expressions: PrintPreviewPaintExpressions,
): PrintPreviewPaintUpdate[] {
  return [
    { layerId: `${sourceId}-line`,   property: 'line-color',   value: expressions.lineColor },
    { layerId: `${sourceId}-line`,   property: 'line-width',   value: expressions.lineWidth },
    { layerId: `${sourceId}-line`,   property: 'line-opacity', value: expressions.lineOpacity },
  ];
}

/** The bit of Mapbox's Map the applier below actually needs. */
export interface PaintablePreviewMap {
  getLayer(id: string): unknown;
  setPaintProperty(layerId: string, property: string, value: unknown): void;
}

/**
 * Repaints an already-added preview route line from the user's current
 * settings. Called when the route style is edited while the print page is
 * open — the layers were built with the old expressions and would otherwise
 * keep them until the map style reloaded.
 *
 * Skips the layer when it is not on the map (no route on the sheet, or a
 * style reload mid-flight), so it is safe to call at any time.
 */
export function applyPrintPreviewRouteStyle(
  map: PaintablePreviewMap | null | undefined,
  sourceId: string | null | undefined,
  expressions: PrintPreviewPaintExpressions = buildPrintPreviewPaintExpressions(),
): void {
  if (!map || !sourceId) return;

  printPreviewPaintUpdates(sourceId, expressions).forEach(({ layerId, property, value }) => {
    if (!map.getLayer(layerId)) return;
    map.setPaintProperty(layerId, property, value);
  });
}

// Re-export for test convenience.
export { DEFAULT_ROUTE_STYLE_SETTINGS };
